import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tab, 
  Tabs, 
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import TransportLineList from '../components/transport/TransportLineList';
import TransportLineForm from '../components/transport/TransportLineForm';
import TransportLineManagement from '../components/transport/TransportLineManagement';
import DocumentManagement from '../components/transport/DocumentManagement';
import Topbar from '../components/Topbar';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TransportAdmin = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Transporte');
  const [tabValue, setTabValue] = useState(0);
  const [transportLines, setTransportLines] = useState([]);
  const [filteredTransportLines, setFilteredTransportLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Optionally clear selection when changing tabs
    // setSelectedLine(null);
  };

  const handleSelectLine = (line) => {
    setSelectedLine(line);
    // Cambiar automáticamente a la pestaña de administración
    setTabValue(1);
  };

  const clearSelectedLine = () => {
    setSelectedLine(null);
    setDocuments([]);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const fetchTransportLines = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/lineas_transporte/`);
      const data = await response.json();
      setTransportLines(data);
    } catch (error) {
      console.error('Error fetching transport lines:', error);
      toast.error('Error al cargar las líneas de transporte');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocuments = async (lineId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/documents/${lineId}`);
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    fetchTransportLines();
  }, []);

  useEffect(() => {
    if (selectedLine) {
      fetchDocuments(selectedLine.id);
    }
  }, [selectedLine]);

  // Apply filter when status filter or transport lines change
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTransportLines(transportLines);
    } else {
      let filtered;
      switch (statusFilter) {
        case 'active':
          filtered = transportLines.filter(line => line.active && !line.blocked);
          break;
        case 'inactive':
          filtered = transportLines.filter(line => !line.active && !line.blocked);
          break;
        case 'blocked':
          filtered = transportLines.filter(line => line.blocked);
          break;
        default:
          filtered = transportLines;
      }
      setFilteredTransportLines(filtered);
    }
  }, [statusFilter, transportLines]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />

      <div className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen`}>
        {/* Replace barra superior with Topbar component */}
        <Topbar 
          
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />

        {/* Contenido principal */}
        <div className="p-6">
          <Typography variant="h4" gutterBottom className="text-gray-700 mb-6">
            Administración de Transportistas
          </Typography>
          
          <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'white',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 120,
                  fontWeight: 'medium',
                }
              }}
            >
              <Tab label="Tablero de control" />
              <Tab label="Administración" />
            </Tabs>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TabPanel value={tabValue} index={0}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => setIsFormOpen(true)}
                      sx={{ 
                        backgroundColor: 'rgb(3 105 161)',
                        '&:hover': {
                          backgroundColor: 'rgb(2 132 199)'
                        }
                      }}
                    >
                      Nueva Línea de Transporte
                    </Button>
                    <FormControl variant="outlined" sx={{ minWidth: 180 }}>
                      <InputLabel>Filtrar por estado</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        label="Filtrar por estado"
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="active">Activos</MenuItem>
                        <MenuItem value="inactive">Inactivos</MenuItem>
                        <MenuItem value="blocked">Bloqueados</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      {filteredTransportLines.length} resultados
                    </Typography>
                  </Stack>
                  <TransportLineList 
                    transportLines={filteredTransportLines} 
                    onSelect={handleSelectLine}
                    onUpdate={fetchTransportLines}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <TransportLineManagement 
                    selectedLine={selectedLine}
                    onSelectLine={setSelectedLine}
                    transportLines={transportLines}
                    onUpdate={fetchTransportLines}
                  />
                </TabPanel>
              </>
            )}
          </Paper>
          {selectedLine && (
            <DocumentManagement 
              documents={documents} 
              lineId={selectedLine?.id} 
              onUpload={fetchDocuments} 
            />
          )}
        </div>

        <TransportLineForm 
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={fetchTransportLines}
        />
      </div>
      <ToastContainer />
    </div>
  );
};

export default TransportAdmin;
